import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useParams, useLocation } from 'react-router-dom';
import _ from 'underscore';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import * as actions from './actions';
import { verifyAuthState } from 'global/actions/authActions';
import { useQueryParams } from 'hooks/useQueryParams';
import { DistributionChart } from 'components/DistributionChart/DistributionChart';
import CategoryDistribution from './components/categoryGraph';
import { useActions } from 'hooks/useActions';
import { Images } from 'assets/images';
import { calculatePercentage, getReportCalculationData } from 'functions/testResult';
import { Spinner } from 'components/Spinner/Spinner';
import InformationModal from 'components/Modals/informationModal';
import TabSelector from 'components/TabSelector/TabSelector';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import MCQAnswersTab from './components/mcqAnswersTab';
import CodeTab from './components/codeTab';
import InfoBox from './components/infoBox';
import TestCases from './components/testCases';
import { modalInformation } from './data';
import { ChangeDepartmentDropdown, DownloadReportDropdown } from './components/buttonDropdown';
import YesNoModal from 'components/Modals/yesNoModal';
import { getConfig } from 'functions/getConfig';
import { userRoles } from 'constants/users';
import { categories } from 'constants/tests';
import { downloadReport } from 'functions/downloadReport';
import getComputedDescription from 'functions/getComputedDescription';
import {
  InfoAndScoreContainer,
  InfoAndScoreBox,
  Heading,
  HeadingUnderLine,
  LabelHeading,
  LabelValue,
  GaugeImage,
  PointerImage,
  ScoreDescriptionContainer,
  PercentageText,
  InfoIcon,
  InfoIconContainer,
  ScoreBoxContainer,
  GaugeAndPointerContainer,
  ActiveTabBox,
  DescriptionHeading,
  Description,
  FreecodeScoreContainer,
  FreecodeScore,
  SeparateContainer,
  SeparateInnerContainer,
  FlexContainer,
  CapsuledTab,
  LeftCapsuledTab,
  RightCapsuledTab,
  QuestionNumberContainer,
  NoQuestionText,
  ButtonContainer,
  FutureSkillLogo,
  InnerBorderPrintContainer,
  BorderPrintContainer,
} from './styles';
import Path from 'routes/Path';

const ReportPage = () => {
  let { id, session } = useParams();
  const navigate = useNavigate();
  const { version, anonymiseReport: anonymiseReportParam, showDescription: showDescriptionParam } = useQueryParams();

  let anonymiseReport = false;
  let showDescription = false;
  if (showDescriptionParam) showDescription = showDescriptionParam === 'false' ? false : true;
  if (anonymiseReportParam) anonymiseReport = anonymiseReportParam === 'false' ? false : true;
  const isCompleteReport = version === 'complete';
  const { pathname } = useLocation();
  let isAnonymiseLinkReport = false;
  if (pathname.includes('anonymiseReport')) {
    isAnonymiseLinkReport = true;
    anonymiseReport = true;
  }

  const { loadReport, updateTicketDepartment, generateAnonymiseLink, disableAnonymiseLink } = useActions(actions);
  let { loading, error, report, updatedTicket, anonymiseIdResponse, disableAnonymiseId } = useSelector(
    (state) => state.ReportPageReducer
  );
  let { userRole } = useSelector((state) => state.auth);

  const [isFreecode, setIsFreecode] = useState(false);
  const [showScoreInstructionsModal, setShowScoreInstructionsModal] = useState(false);
  const [bestRunInput, setBestRunInput] = useState({});
  const [timeUsed, setTimeUsed] = useState(null);
  const [completedAt, setCompletedAt] = useState(null);
  const [candidatePercentage, setCandidatePercentage] = useState(0);
  const [candidateRatingGauge, setCandidateRatingGauge] = useState(0);
  const [activeIndexTab, setActiveIndexTab] = useState(0);
  const [modalInformationKey, setModalInformationKey] = useState('totalStatistics');
  const [showFullPopulation, setShowFullPopulation] = useState(true);
  const [totalScore, setTotalScore] = useState(0);
  const [totalMaxScore, setTotalMaxScore] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [anonymiseId, setAnonymiseId] = useState('');

  // To avoid extra blank page in the candidate version of the report.
  // https://github.com/puppeteer/puppeteer/issues/589
  if (version === 'candidate') {
    let a = document.getElementById('root');
    a.style.overflow = 'hidden';
    a.style.marginBottom = '-1px';
  }

  useEffect(() => {
    if (session) {
      document.cookie = `fsSession=${session}; Domain=${getConfig().COOKIE}; Max-Age=50; Path=/; SameSite=Lax`;
      verifyAuthState();
    }
  }, [session]);

  useEffect(() => {
    loadReport(id, isAnonymiseLinkReport);
  }, [loadReport, id, isAnonymiseLinkReport]);

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (updatedTicket._id) toast.success('Department changed successfully');
  }, [updatedTicket]);

  const copyAnonymiseLink = useCallback((id) => {
    navigator.clipboard.writeText(`${window.location.origin}/anonymiseReport/${id}`);
    toast.success('Link has been copied to clipboard');
  }, []);

  useEffect(() => {
    if (anonymiseIdResponse) {
      setAnonymiseId(anonymiseIdResponse);
      copyAnonymiseLink(anonymiseIdResponse);
    }
  }, [anonymiseIdResponse, copyAnonymiseLink]);

  useEffect(() => {
    if (disableAnonymiseId) {
      setAnonymiseId('');
      toast.success('Anonymise link disabled successfully');
    }
  }, [disableAnonymiseId]);

  useEffect(() => {
    if (!report.ticket) return;
    setIsFreecode(report.test.category === categories.CODE.category);
    if (report.ticket.anonymiseId) setAnonymiseId(report.ticket.anonymiseId);

    let percentage = calculatePercentage(report.test, report.ticket, false);
    setCandidatePercentage(Math.round(percentage));
    setCandidateRatingGauge(Math.round(percentage) * (180 / 100));

    let finalSubmit = _.findWhere(report.answers, { isFinalSubmit: true });
    let bestAfterDeadline = _.findWhere(report.answers, { isBestAfterDeadline: true });
    let bestRun = report?.ticket?.bestRun ? _.findWhere(report.answers, { isBestRun: true }) : finalSubmit;
    setBestRunInput(bestRun);

    let candidateDoneAt =
      report.ticket.completedAt ||
      bestAfterDeadline?.timeOfRequest ||
      finalSubmit?.timeOfRequest ||
      report.ticket.extendedDeadline ||
      report.ticket.deadlineAt ||
      (report.ticket.startedAt ? moment(moment(report.ticket.startedAt) + 60000 * report.test.duration) : false);
    let timeUsed = candidateDoneAt
      ? Math.round(moment(candidateDoneAt).diff(moment(report.ticket.startedAt), 'minutes', true))
      : report.test.duration;
    setCompletedAt(candidateDoneAt);
    setTimeUsed(timeUsed);

    if (report.test.category === categories.CODE.category) {
      let totalLeveLScores = _.reduce(
        bestRun.levelScores,
        function (memo, num) {
          return memo + (num ? num : 0);
        },
        0
      );
      setTotalScore(totalLeveLScores);

      let totalLevelMaxScore = _.reduce(
        bestRun.levelMaxScores,
        function (memo, num) {
          return memo + (num ? num : 0);
        },
        0
      );
      setTotalMaxScore(totalLevelMaxScore);
    } else if ([categories.MULTICHOICE.category, categories.GENERAL_SKILLS.category].includes(report.test.category)) {
      setTotalMaxScore(report.ticket.totalQuestions ? report.ticket.totalQuestions : report.test.scale);
    }
  }, [report]);

  const renderScoreBox = () => {
    return (
      <ScoreBoxContainer>
        <GaugeAndPointerContainer>
          <GaugeImage src={Images.gaugeModal} />
          <PointerImage angle={candidateRatingGauge} />
        </GaugeAndPointerContainer>
        <ScoreDescriptionContainer>
          {!isFreecode && (
            <>
              <PercentageText>{candidatePercentage}%</PercentageText>
              <LabelValue>Better than {candidatePercentage}% of the reported results</LabelValue>
            </>
          )}
          {isFreecode && (
            <>
              <PercentageText>{candidatePercentage}%</PercentageText>
              <LabelValue>
                The candidate scored {report.ticket.result} / {totalMaxScore}
              </LabelValue>
              <br />
              <LabelValue>which is better than {candidatePercentage}% of all the reported results</LabelValue>
            </>
          )}
        </ScoreDescriptionContainer>
        <InfoIconContainer>
          <InfoIcon
            icon="fa fa-info-circle"
            onClick={() => {
              setModalInformationKey('totalStatistics');
              setShowScoreInstructionsModal(true);
            }}
          />
        </InfoIconContainer>
      </ScoreBoxContainer>
    );
  };

  const renderDescriptionBox = () => {
    return (
      <ActiveTabBox forPrint={true}>
        <BorderPrintContainer>
          <InnerBorderPrintContainer>
            <Heading>TASK DESCRIPTION</Heading>
            <HeadingUnderLine />
            <DescriptionHeading>About {report.test.name}</DescriptionHeading>
            <Description dangerouslySetInnerHTML={{ __html: report.test.description }}></Description>
            {isFreecode && (
              <>
                <DescriptionHeading>Candidate Description</DescriptionHeading>
                <Description
                  dangerouslySetInnerHTML={{
                    __html: getComputedDescription(
                      report.test.candidateDescription,
                      categories.CODE.category,
                      'python'
                    ),
                  }}
                ></Description>
              </>
            )}
            {report.test.adaptiveTest && (
              <>
                <DescriptionHeading>About Adaptive Tests</DescriptionHeading>
                <Description dangerouslySetInnerHTML={{ __html: report.config.value }}></Description>
              </>
            )}
          </InnerBorderPrintContainer>
        </BorderPrintContainer>
      </ActiveTabBox>
    );
  };

  const renderStatistics = () => {
    if (isFreecode) {
      return (
        <ActiveTabBox>
          <Heading>BEST SCORE</Heading>
          <HeadingUnderLine />
          <FreecodeScoreContainer>
            <LabelHeading>{anonymiseReport ? 'Candidate' : report.ticket.name || report.candidate.name}</LabelHeading>
            <LabelValue>&nbsp;got</LabelValue>
            <FreecodeScore>
              {report.ticket.result} / {report.ticket.totalQuestions ? report.ticket.totalQuestions : report.test.scale}
            </FreecodeScore>
            <LabelValue>in score on this assignment</LabelValue>
            <InfoIconContainer>
              <InfoIcon
                icon="fa fa-info-circle"
                onClick={() => {
                  setModalInformationKey('candidateScore');
                  setShowScoreInstructionsModal(true);
                }}
              />
            </InfoIconContainer>
          </FreecodeScoreContainer>
        </ActiveTabBox>
      );
    } else {
      return renderDistribution(false);
    }
  };

  const renderDistribution = (extraBoxWithShadow) => {
    let reportData = getReportCalculationData(report.test, report.ticket, false);
    return (
      <SeparateContainer
        extraBoxWithShadow={extraBoxWithShadow}
        maxHeightForPrint={!extraBoxWithShadow ? '480px' : false}
      >
        <SeparateInnerContainer extraBoxWithShadow={extraBoxWithShadow}>
          <ActiveTabBox>
            <FlexContainer handleSmallDevice>
              <Heading>DISTRIBUTION</Heading>
              {reportData.hasDistribution && (
                <CapsuledTab>
                  <LeftCapsuledTab active={showFullPopulation} onClick={() => setShowFullPopulation(true)}>
                    Full population
                  </LeftCapsuledTab>
                  <RightCapsuledTab active={!showFullPopulation} onClick={() => setShowFullPopulation(false)}>
                    {anonymiseReport ? 'Company population' : `${report.company.name}'s population`}
                  </RightCapsuledTab>
                </CapsuledTab>
              )}
            </FlexContainer>
            <HeadingUnderLine />
            <DistributionChart
              test={report.test}
              showTotalTestsTaken={true}
              showFullPopulation={showFullPopulation}
              ticket={report.ticket}
            />
            <InfoIconContainer>
              <InfoIcon
                icon="fa fa-info-circle"
                marginLeft="50"
                marginTop="30"
                onClick={() => {
                  setModalInformationKey('totalDistribution');
                  setShowScoreInstructionsModal(true);
                }}
              />
            </InfoIconContainer>
          </ActiveTabBox>
        </SeparateInnerContainer>
      </SeparateContainer>
    );
  };

  const scrollToQuestion = (index) => {
    let element = document.getElementById(`question${index}`);
    let headerOffset = 60;
    let elementPosition = element.getBoundingClientRect().top;
    let offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const renderQuestionsBox = () => {
    return report.questions.map((question, index) => (
      <QuestionNumberContainer key={question._id} correct={question.correct} onClick={() => scrollToQuestion(index)}>
        {index + 1}
      </QuestionNumberContainer>
    ));
  };

  const renderAnswersBox = () => {
    if (isFreecode)
      return (
        <CodeTab
          codeInputs={[...(report?.answers ?? [])].sort(
            (i1, i2) => new Date(i1.timeOfRequest) - new Date(i2.timeOfRequest)
          )}
          ticket={report.ticket}
          bestRunInput={bestRunInput}
          anonymiseReportId={anonymiseReport ? id : false}
          setDisplayedInput={(input) => setBestRunInput(input)}
        />
      );
    return (
      <ActiveTabBox forPrint={true}>
        {report.totalAnswered ? (
          <BorderPrintContainer>
            <InnerBorderPrintContainer>
              <Heading>ANSWERS</Heading>
              <HeadingUnderLine />
              {renderQuestionsBox()}{' '}
            </InnerBorderPrintContainer>
          </BorderPrintContainer>
        ) : (
          <NoQuestionText>No questions answered</NoQuestionText>
        )}
      </ActiveTabBox>
    );
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      <Container column>
        {loading && <Spinner />}
        {!loading && (
          <>
            {showConfirmationModal && (
              <YesNoModal
                modalInfo={modalInfo}
                confirm={(isConfirmed) => {
                  setShowConfirmationModal(false);
                  if (isConfirmed) {
                    if (modalInfo.isDisableLinkModal) disableAnonymiseLink(anonymiseId);
                    else if (modalInfo.isGenerateLinkModal) generateAnonymiseLink(id);
                    else updateTicketDepartment(report.ticket._id, modalInfo.department);
                  }
                  setModalInfo({});
                }}
              />
            )}
            {showScoreInstructionsModal && modalInformationKey && (
              <InformationModal
                title={modalInformation[modalInformationKey]?.title}
                textHTML={modalInformation[modalInformationKey]?.textHTML}
                closeModal={() => setShowScoreInstructionsModal(false)}
              />
            )}
            {version && <FutureSkillLogo id="isDownloadable" src={Images.logoBlue} />}
            <ButtonContainer>
              {!anonymiseReport && (
                <Button small primaryBlue primaryGreenBorder margin="5px" onClick={() => navigate(Path.testsPage)}>
                  <i className="fa fa-arrow-left"></i>&nbsp;Back
                </Button>
              )}
              <DownloadReportDropdown
                isFreecode={isFreecode}
                anonymiseReport={anonymiseReport}
                downloadReport={async (type, anonymiseReport, showDescription) => {
                  if (isPdfDownloading) return toast.error('Report already downloading');
                  setIsPdfDownloading(true);
                  await downloadReport(
                    id,
                    report.ticket.name ?? report.candidate.name,
                    report.test.name,
                    type,
                    anonymiseReport,
                    showDescription,
                    isAnonymiseLinkReport
                  );
                  setIsPdfDownloading(false);
                }}
              />
              {!anonymiseReport &&
                report?.company?.departments?.length > 1 &&
                ['super-admin', 'company-admin'].includes(userRole) && (
                  <ChangeDepartmentDropdown
                    ticket={report.ticket}
                    departments={report.company.departments}
                    onDepartmentChange={(d) => {
                      setShowConfirmationModal(true);
                      setModalInfo({
                        title: 'Confirmation',
                        body: `Do you want to move the ticket to ${d.label} department?`,
                        department: d.value,
                      });
                    }}
                  />
                )}
              {!anonymiseReport && (
                <Button
                  small
                  primaryBlue
                  primaryGreenBorder
                  margin="5px"
                  onClick={() => {
                    if (anonymiseId) copyAnonymiseLink(anonymiseId);
                    else {
                      setShowConfirmationModal(true);
                      setModalInfo({
                        title: 'Confirmation',
                        body: `Do you want to generate an anonymized link?`,
                        isGenerateLinkModal: true,
                      });
                    }
                  }}
                >
                  <i className="fa fa-eye-slash" aria-hidden="true"></i>
                  &nbsp;{anonymiseId ? 'Copy Anonymized Link' : 'Generate Anonymized Link'}
                </Button>
              )}
              {!anonymiseReport && anonymiseId && (
                <Button
                  small
                  primaryRed
                  margin="5px"
                  onClick={() => {
                    setShowConfirmationModal(true);
                    setModalInfo({
                      title: 'Confirmation',
                      body: `Do you want to disable the anonymized link?`,
                      isDisableLinkModal: true,
                    });
                  }}
                >
                  <i className="fa fa-ban" aria-hidden="true"></i>
                  &nbsp;Disable Anonymise Link
                </Button>
              )}
            </ButtonContainer>
            <InfoAndScoreContainer>
              <InfoAndScoreBox>
                <Heading>INFO</Heading>
                <HeadingUnderLine />
                {report.ticket && (
                  <InfoBox
                    report={report}
                    isFreecode={isFreecode}
                    totalMaxScore={totalMaxScore}
                    bestRunInput={bestRunInput}
                    timeUsed={timeUsed}
                    completedAt={completedAt}
                    anonymiseReport={anonymiseReport}
                  />
                )}
              </InfoAndScoreBox>
              <InfoAndScoreBox>
                <Heading>{isFreecode && report?.ticket?.bestRun ? 'BEST SCORE' : 'SCORE'}</Heading>
                <HeadingUnderLine />
                {report.ticket && renderScoreBox()}
              </InfoAndScoreBox>
            </InfoAndScoreContainer>
            {report.ticket && (
              <TabSelector
                isReportPage={true}
                items={[
                  { label: 'STATISTICS', component: renderStatistics() },
                  { label: 'DESCRIPTION', component: renderDescriptionBox() },
                  { label: 'ANSWERS', component: renderAnswersBox() },
                ]}
                getActiveIndex={(index) => setActiveIndexTab(index)}
              ></TabSelector>
            )}
            {isFreecode && activeIndexTab === 0 && report?.ticket && renderDistribution(true)}
            {!isFreecode && activeIndexTab === 0 && report.test && report.totalAnswered > 0 && (
              <CategoryDistribution
                test={report.test}
                report={report}
                onInfoClick={() => {
                  setModalInformationKey('testCategories');
                  setShowScoreInstructionsModal(true);
                }}
              />
            )}
            {isFreecode && activeIndexTab === 2 && bestRunInput?._id && <TestCases selectedInput={bestRunInput} />}
            {!isFreecode && activeIndexTab === 2 && report.totalAnswered > 0 && (
              <MCQAnswersTab report={report} isSuperAdmin={userRole === userRoles.SUPER_ADMIN} />
            )}
            {/* For pdf download */}
            {report.test && version && isCompleteReport && showDescription && renderDescriptionBox()}
            {report.test && version && isCompleteReport && renderAnswersBox()}
            {report.test && version && isCompleteReport && !isFreecode && report.totalAnswered > 0 && (
              <MCQAnswersTab report={report} userRole={userRole} />
            )}
            {report.test && version && isFreecode && (
              <TestCases selectedInput={bestRunInput} totalScore={totalScore} totalMaxScore={totalMaxScore} />
            )}
          </>
        )}
      </Container>
    </Wrapper>
  );
};

export default ReportPage;
